import type { ColorList, PartialDeep } from '@nuvolo/nuux-theme/types';
import type { ExtendedNuvoTheme } from './types';
import { ReactComponent as NuvoloLogoLight } from '#/static/NuvoloLogoLight.svg';

export const lightThemeLogo = NuvoloLogoLight;

export const lightColors: PartialDeep<ExtendedNuvoTheme['colors']> = {
  primary: {
    light3: '#EAEAEA',
    light2: '#EAEAEA',
    light: '#EAEAEA',
    middle: '#6B6B6B',
    dark: '#1C1C1C',
    dark2: '#1C1C1C',
    dark3: '#1C1C1C',
  },
  secondary: {
    light3: '#EAEAEA',
    light2: '#EAEAEA',
    light: '#6B6B6B',
    middle: '#6B6B6B',
    dark: '#6B6B6B',
    dark2: '#2C2C2C',
    dark3: '#1C1C1C',
  },
  accent: {
    light3: '#FEE8E8',
    light2: '#FEE8E8',
    light: '#FEE8E8',
    middle: '#F85F5F',
    dark: '#F85F5F',
    dark2: '#F74848',
    dark3: '#F63131',
  },
  ui: {
    light3: '#F5F5F5',
    light2: '#E6E6E8',
    light: '#C0C1C5',
    middle: '#C0C1C5',
    dark: '#6A6C71',
    dark2: '#02070C',
    dark3: '#02070C',
  },
  contrast: {
    light: '#FFFFFF',
    dark: '#000000',
  },
  warn: {
    light3: '#FFDCC8',
    light2: '#FFDCC8',
    light: '#FD6108',
    middle: '#FD6108',
    dark: '#FD6108',
    dark2: '#FD6108',
    dark3: '#FD6108',
  },
  error: {
    light3: '#FDD1D1',
    light2: '#FDD1D1',
    light: '#F63131',
    middle: '#F63131',
    dark: '#F63131',
    dark2: '#F63131',
    dark3: '#F63131',
  },
  success: {
    light3: '#C9F3C9',
    light2: '#C9F3C9',
    light: '#52C352',
    middle: '#52C352',
    dark: '#52C352',
    dark2: '#52C352',
    dark3: '#52C352',
  },
  info: {
    light3: '#D1E1FD',
    light2: '#D1E1FD',
    light: '#3176F6',
    middle: '#3176F6',
    dark: '#3176F6',
    dark2: '#3176F6',
    dark3: '#3176F6',
  },
  reporting: {
    darkGray: '#F4312A',
    blue: '#FE9801',
    aqua: '#21B937',
    green: '#FDCE01',
    gold: '#04B1AE',
    orange: '#065352',
    red: '#B054DC',
    pink: '#FF00F5',
    purple: '#55C0EF',
    gray: '#5657D9',
    lightBlue: '#FE3661',
    lightAqua: '#5DADC7',

    //missing colors - made them all the last color (#A3835F)
    lightGreen: '#A3835F',
    lightGold: '#A3835F',
    lightOrange: '#A3835F',
    lightRed: '#A3835F',
    // Check missing colors^^^

    lightPink: '#EFE8FA',
    lightPurple: '#BEA3EA',
    lightGray: '#C0C1C5',
    thresholdGreen: '#21B937',
    thresholdRed: '#F4312A',

    // custom wo priority
    priorityCritical: '#F63131',
    priorityHigh: '#FD6108',
    priorityMedium: '#FDCE01',
    priorityLow: '#21B937',
    priorityVeryLow: '#C9F3C9',
    priorityUnknown: '#C0C1C5',

    // custom finding priority
    findPriorityPlaning: '#3176F6',
    findPriorityLow: '#21B937',
    findPriorityMedium: '#FDCE01',
    findPriorityHigh: '#FD6108',
    findPriorityCritical: '#F63131',
    findPriorityUnknown: '#C0C1C5',
  } as ColorList,
  custom: {
    constantBlue: '#1D8AFE',
    constantTeal: '#6BB5CC',
    constantGray: '#A5A7A7',
    link: '#2D6ADB',
    formBackground: '#F4F4F4',
    selectedBackground: '#1C1C1C',
    paginationText: '#02070C',
    dataGridBackground: '#FFFFFF',
    dataGridPageBackground: '#FFFFFF',
    formGroupBackground: '#FFFFFF',
    dataGridHeaderBackground: '#F4F4F4',
    columnChooserBackground: '#FFFFFF',
    columnChooserItemBackground: '#FFFFFF',
    dataGridHeaderText: '#6B6B6B',
    hoveredBackground: '#EAEAEA',
    cardBackground: '#FFFFFF',
    contrastText: '#000000',
    invertedText: '#FFFFFF',
    invertedBackground: '#FFFFFF',
    lightContrastText: '#C0C1C5',
    formInputBackground: '#FFFFFF',
    formInputBorder: '#EAEAEA',
    formInputPlaceholderColor: '#A6A9AD',
    formDisabledInputColor: '#7B7E83',
    inputIconBackground: '#EAEAEA',
    switchBackground: '#F5F5F5',
    chipSecondaryBackground: '#E6E6E8',
    chipSecondaryText: '#6A6C71',
    defaultIconBackground: '#F5F5F5',
    fancyDropDownBackground: '#FFFFFF',
    focusItemBackground: '#E6E6E8', // Tmm select card mostly
    selected: '#181a1c', // mobile list cards selected border color
    hoverColor: '#2F3032', // app menu hover color definition
    dashboardConfigItemBorder: '#212326',
    formImageWrapperBackground: '#121416',

    // Gray tones
    labelGray: '#74754C', //profile menu labels
    mediumGray: '#A5A7A7', // organization wrapper, subtle text
    iconGray: '#7F7F7F', // icon colors
    tabGray: '#323232', // tab backgrounds
    badgeGray: '#151921', // badge backgrounds
    constLightGray: '#A6A9AD', // color for the "x" clear button in reference fields

    // Borders
    borderLight: '#F0F0F0', // light borders

    // Status backgrounds
    statusPlanned: '#FFFBE6', // light yellow background for planned status
    statusWoCreated: '#EFDDF8', // light purple background for wo_created status

    // Accent colors
    blueAccent: '#0F67FF', // blue accent for selections
    blueBackground: '#D9EEFC', // light blue background
  },
};
